import {
    createCompany,
    deleteCompanyEmploymentRelation,
    fetchCompanies,
    fetchCompany,
    fetchCompanyLinkedMFHs,
    fetchCompanyMFHRelations,
    fetchContractorCapacities,
    fetchContractorCapacityWeeks,
    fetchSubcompaniesAndEstates,
    mergeCompanies,
    saveCompanyEmploymentRelation,
    updateCompany,
    updateCompanyMFHRelations,
} from '@/api/company.api';
import { fetchContractorCatalogueItems, fetchContractorPricings } from '@/api/renovation.api';
import {
    type CompanyExpandOpts,
    type CompanyMFHRelationExpandOpts,
    type CompanyOrContactRelation,
    CompanyType,
    type FetchContractorCapacityWeeksParams,
    type ICompany,
    type ICompanyEmploymentRelation,
    type IContactPerson,
    type IFetchCompanyMFHParams,
    type IFetchCompanyParams,
    type IFindSubcompaniesAndEstatesParams,
    type IFindSubcompaniesAndEstatesResults,
} from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRef, computed, unref } from 'vue';

const ONE_HOUR_MS = 60 * 60 * 1000;

export const usePurchasingEntities = (expandOptsOrRef?: MaybeRef<IFetchCompanyParams['expand']>, queryEnabled: MaybeRef<boolean> = true) => {
    const expandOpts = computed(() => unref(expandOptsOrRef));
    const enabled = computed(() => unref(queryEnabled));

    return useQuery(
        ['purchasing-entities', expandOpts],
        async () => {
            const { companies } = await fetchCompanies({ type: CompanyType.PurchasingEntity, expand: expandOpts.value } as IFetchCompanyParams['query'] &
                IFetchCompanyParams['expand']);
            return companies;
        },
        {
            cacheTime: ONE_HOUR_MS * 6,
            initialData: [],
            enabled,
        },
    );
};

export const useDistributionPartners = (expandOptsOrRef?: MaybeRef<IFetchCompanyParams['expand']>) => {
    const expandOpts = computed(() => unref(expandOptsOrRef));
    return useQuery(
        ['distribution-partners', expandOptsOrRef],
        async () => {
            const { companies } = await fetchCompanies({ type: CompanyType.DistributionPartner, expand: expandOpts.value } as IFetchCompanyParams['query'] &
                IFetchCompanyParams['expand']);
            return companies;
        },
        {
            cacheTime: ONE_HOUR_MS * 6,
        },
    );
};

export const useDistributionPartnersForExistingSalesPackagesOnly = (expand?: IFetchCompanyParams['expand']) => {
    return useQuery(['distribution-partners-for-existing-sales-package'], async () => {
        const { companies } = await fetchCompanies({
            type: CompanyType.DistributionPartner,
            existingSalesPackagesOnly: true,
            expand,
        } as IFetchCompanyParams['query'] & IFetchCompanyParams['expand']);
        return companies;
    });
};

export const useCompany = (companyIdOrRef?: MaybeRef<number>, optsOrRef?: MaybeRef<{ expand: CompanyExpandOpts }>) => {
    const companyId = computed(() => unref(companyIdOrRef));
    const opts = computed(() => unref(optsOrRef));
    const queryEnabled = computed(() => !!companyId.value);

    const queryClient = useQueryClient();

    const query = useQuery(['company', companyId, opts], async (): Promise<ICompany> => fetchCompany(companyId.value, opts.value), {
        enabled: queryEnabled,
        initialData: null,
    });

    const { mutateAsync: createCompanyMutation } = useMutation(
        async (payload: {
            company: ICompany;
            employees?: Partial<IContactPerson | ICompanyEmploymentRelation>[];
            estateId?: number;
            mfhId?: number;
            relationName?: CompanyOrContactRelation;
        }) => createCompany(payload),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['company', companyId, opts]);
                queryClient.invalidateQueries(['companies']);
            },
        },
    );

    const { mutateAsync: updateCompanyMutation } = useMutation(
        async (payload: { company: ICompany; employees?: Partial<IContactPerson | ICompanyEmploymentRelation>[]; estateId?: number }) =>
            updateCompany(payload.company.companyId, payload),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['company', companyId, opts]);
                queryClient.invalidateQueries(['companies']);
            },
        },
    );

    const { mutateAsync: mergeCompaniesMutation } = useMutation(
        async (payload: { company: ICompany; companyIds: number[]; employees: Partial<IContactPerson | ICompanyEmploymentRelation>[] }) =>
            mergeCompanies(payload),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['companies']);
            },
        },
    );

    const createOrUpdateCompany = async (payload: {
        company: ICompany;
        employees?: Partial<IContactPerson | ICompanyEmploymentRelation>[];
        estateId?: number;
    }) => {
        if (payload.company.companyId) {
            return updateCompanyMutation(payload);
        }
        return createCompanyMutation(payload);
    };

    return {
        ...query,
        createCompany: createCompanyMutation,
        updateCompany: updateCompanyMutation,
        createOrUpdateCompany,
        mergeCompanies: mergeCompaniesMutation,
    };
};

export const useSubcompaniesAndEstates = (companyId?: MaybeRef<number>, opts?: MaybeRef<Omit<IFindSubcompaniesAndEstatesParams, 'companyId'>>) => {
    const queryEnabled = computed(() => !!unref(companyId));

    return useQuery(
        ['subcompanies-and-estates', companyId, opts],
        async (): Promise<IFindSubcompaniesAndEstatesResults> => fetchSubcompaniesAndEstates({ companyId: unref(companyId), ...unref(opts) }),
        {
            enabled: queryEnabled,
            initialData: { company: null, subcompanies: [], estates: [] },
        },
    );
};

export const useCompanies = (companyFetchParams: MaybeRef<IFetchCompanyParams['query'] | undefined>, expandOptsOrRef?: MaybeRef<CompanyExpandOpts>) => {
    const enabled = computed(() => !!unref(companyFetchParams));

    return useQuery(
        ['companies', companyFetchParams, expandOptsOrRef],
        async (): Promise<{ companies: ICompany[]; total: number }> => {
            const params = { ...unref(companyFetchParams), expand: unref(expandOptsOrRef) as any };

            return fetchCompanies(params);
        },
        {
            //Necessary for CompanyTable to avoid blinking when changing pages
            placeholderData: { companies: [], total: 0 },
            keepPreviousData: true,
            staleTime: 1000,
            enabled,
        },
    );
};

/**
 * Asset partners selectable for investment-case purposes (IC config page, IC creation, copy-and-change-partner).
 * Companies with `excludeFromIC` set are hidden here without affecting other asset-partner listings (filters, MFH assignment, etc.).
 */
export const useAssetPartnersForInvestmentCases = () => {
    return useQuery(
        ['asset-partners-for-investment-cases'],
        async () => {
            const { companies } = await fetchCompanies({
                showInactive: false,
                type: CompanyType.AssetPartner,
                expand: { select: ['companyId', 'name', 'excludeFromIC'] },
            } as IFetchCompanyParams['query'] & IFetchCompanyParams['expand']);
            return companies.filter(company => !company.excludeFromIC);
        },
        {
            cacheTime: ONE_HOUR_MS * 6,
            initialData: [],
        },
    );
};

export const useCompanyEmploymentRelation = () => {
    const { mutateAsync: saveCompanyEmploymentRelationMutation } = useMutation(
        async (companyEmploymentRelation: ICompanyEmploymentRelation) => saveCompanyEmploymentRelation(companyEmploymentRelation),
        {},
    );

    const { mutateAsync: deleteCompanyEmploymentRelationMutation } = useMutation(
        async (companyEmploymentRelation: ICompanyEmploymentRelation) => deleteCompanyEmploymentRelation(companyEmploymentRelation),
        {},
    );

    return {
        saveCompanyEmploymentRelation: saveCompanyEmploymentRelationMutation,
        deleteCompanyEmploymentRelation: deleteCompanyEmploymentRelationMutation,
    };
};

export const useContractorCapacityWeeks = (paramsOrRef?: MaybeRef<FetchContractorCapacityWeeksParams>) => {
    const params = computed(() => unref(paramsOrRef));

    return useQuery(['contractor-capacity-weeks', params], () => fetchContractorCapacityWeeks(params.value), {
        initialData: [],
    });
};

export const useContractorCapacities = (paramsOrRef?: MaybeRef<{ contractorId?: number } | undefined>) => {
    const params = computed(() => unref(paramsOrRef));

    return useQuery(['contractor-capacities', params], () => fetchContractorCapacities(params.value), {
        initialData: [],
    });
};

export const useContractorPricings = (contractorIdOrRef?: MaybeRef<number>, opts?: { withCatalogueItemPrices: boolean }) => {
    const contractorId = computed(() => unref(contractorIdOrRef));
    const isEnabled = computed(() => !!contractorId?.value);

    return useQuery(['contractorPricing', contractorId, opts], () => fetchContractorPricings(contractorId.value, opts), {
        enabled: isEnabled,
        initialData: [],
    });
};

export const useContractorCatalogueItems = (contractorIdOrRef?: MaybeRef<number>) => {
    const contractorId = computed(() => unref(contractorIdOrRef));
    const isEnabled = computed(() => !!contractorId?.value);

    return useQuery(['contractorCatalogueItems', contractorId], () => fetchContractorCatalogueItems(contractorId.value), {
        enabled: isEnabled,
        initialData: [],
    });
};

export const useCompanyMfhs = (companyId: number, params?: IFetchCompanyMFHParams) => {
    return useQuery(['company-mfh', companyId], () => fetchCompanyLinkedMFHs(companyId, params), {
        initialData: {
            mfhs: [],
            total: 0,
        },
    });
};

export const useCompanyMFHRelations = (
    mfhIdOrRef: MaybeRef<number | undefined>,
    expandOptsOrRef: MaybeRef<CompanyMFHRelationExpandOpts>,
    paramsOrRef?: MaybeRef<{ companyId?: number; relationName?: CompanyOrContactRelation } | undefined>,
    queryEnabled: MaybeRef<boolean> = true,
) => {
    const mfhId = computed(() => unref(mfhIdOrRef));
    const expandOpts = computed(() => unref(expandOptsOrRef));
    const params = computed(() => unref(paramsOrRef));
    const enabled = computed(() => unref(queryEnabled) && !!mfhId.value);

    return useQuery(['company-mfh-relations', mfhId, expandOpts, params], () => fetchCompanyMFHRelations(mfhId.value!, params.value, expandOpts.value), {
        enabled,
        initialData: [],
    });
};

export const useUpdateCompanyMFHRelations = () => {
    const queryClient = useQueryClient();

    return useMutation((params: { mfhId: number; relationName: CompanyOrContactRelation; companyIds: number[] }) => updateCompanyMFHRelations(params), {
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries(['company-mfh-relations', variables.mfhId]);
            queryClient.invalidateQueries(['company-mfh']);
        },
    });
};
